import React, { CSSProperties } from 'react';
import { Box, BoxProps } from '@withjoy/joykit';
import { XY, startDrag } from './startDrag';
import { useSurface } from './useSurface';
import { LayerData } from '../../components/Layer.types';
import { TextMeasurer } from './TextMeasurer';
import { QRCodeMeasurer } from './QRCodeMeasurer';
import { PPI } from './config';
import { PageDimensions } from '../../CardCustomizer.types';
import { clamp, getLimits, rotateVector } from './Adornments.math';
import { getDimensions, layerLayoutToCss } from '@apps/card/utils';
import { useCustomizationContext } from '../../useCardCustomization';

const RADIUS = 6;
const DIAMETER = RADIUS * 2;

// 1/8" where it's not safe to place text. This is in addition to the 1/8" bleed. Source: Taylor.
const SAFE_MARGIN_INCHES = 0.125;
export function calcSafeMargin(dimensions?: PageDimensions) {
  return (dimensions?.safeZoneMargin ?? SAFE_MARGIN_INCHES) * PPI;
}

type HandleProps = Pick<BoxProps, 'cursor' | 'onPointerDown' | 'gridArea'>;

const Handle = (props: HandleProps) => {
  return (
    <Box
      backgroundColor="white"
      border="2px solid hsl(100, 30%, 30%, 1)"
      borderRadius={RADIUS}
      boxShadow="0px 1px 8px 0px rgba(0, 0, 0, 0.25)"
      pointerEvents="all"
      touchAction="none"
      {...props}
    ></Box>
  );
};

const Bar = (props: HandleProps) => {
  return (
    <Box
      pointerEvents="all"
      touchAction="none"
      {...props}
    />
  );
};

interface AdornmentsProps {
  card: {
    dimensions?: PageDimensions;
    layers: readonly LayerData[];
  };
  update: (scopedUpater: (scopedDraft: { layers: readonly LayerData[] }) => void) => void;
}
export function Adornments({ card, update }: AdornmentsProps) {
  const [draft] = useCustomizationContext(draft => draft);
  const dimensions = getDimensions(draft.config.card);

  const [{ scale, activeLayerIndex, editingLayerIndex }, updateSurface] = useSurface();

  if (activeLayerIndex === undefined || !card.layers[activeLayerIndex]) return null;
  const layer = card.layers[activeLayerIndex];

  const { layout } = layer;
  const { x: ox, y: oy, width: ow } = layout; // 'o' for original
  const rotation = layout.rotation ?? 0;

  const MIN_SIZE = layer.type === 'qrcode' ? 72 : 16;
  const [xmin, xmax] = getLimits(dimensions.width * PPI, dimensions.height * PPI, calcSafeMargin(card.dimensions), layout);

  const onDrag = (surfaceDelta: XY, mask: 'leading' | 'trailing') => {
    const delta = rotateVector(surfaceDelta, rotation); // layer-rotation-relative
    if (editingLayerIndex !== undefined) {
      updateSurface(draft => {
        draft.editingLayerIndex = undefined;
      });
    }

    // prettier-ignore
    update(draft => {
      // layer-relative coodinates
      const x1 = mask === 'leading'  ? clamp(xmin    , 0  + delta.x / scale, -MIN_SIZE + ow        ) :  0;
      const x2 = mask === 'trailing' ? clamp(MIN_SIZE, ow + delta.x / scale,             ow + -xmax) : ow;

      // surface-relative coordiantes
      const layerXYDelta = rotateVector({ x: x1, y: 0 }, -rotation);
      draft.layers[activeLayerIndex].layout = {
        ...layout,
        x: ox + layerXYDelta.x,
        y: oy + layerXYDelta.y,
        width: x2 - x1
      };
    });
  };

  // Stepped to avoid odd-looking 1 x 1.1 ratio handle.
  const handleHeight = layout.height >= DIAMETER * 4 ? DIAMETER * 2 : DIAMETER;

  const isLayerEditing = editingLayerIndex === activeLayerIndex;
  const isLayerResizable = layer.type !== 'image';

  return (
    <Box
      position="absolute"
      pointerEvents="none"
      cursor="move"
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      backgroundColor="rgba(0, 100%, 50%, 0.1)"
      border="1px solid hsl(100, 50%, 20%, 0.4)"
      style={layerLayoutToCss(layout, scale)}
    >
      {layer.type === 'text' && (
        <TextMeasurer
          layer={layer}
          onHeightChange={height => {
            update(page => {
              page.layers[activeLayerIndex].layout.height = height;
            });
          }}
        />
      )}
      {layer.type === 'qrcode' && (
        <QRCodeMeasurer
          layer={layer}
          onHeightChange={height => {
            update(page => {
              page.layers[activeLayerIndex].layout.height = height;
            });
          }}
        />
      )}
      <Box
        position="absolute"
        inset={`${-RADIUS}px`}
        gridTemplateAreas='"nw n ne" "w . e" "sw s se"'
        display="grid"
        gridTemplateColumns={`${RADIUS * 2}px 1fr ${RADIUS * 2}px`}
        gridTemplateRows={`1fr ${handleHeight}px 1fr`}
      >
        {!isLayerEditing && isLayerResizable && (
          <>
            <Bar
              gridArea="1 / 1/ -1"
              cursor={getRotatedCursor(rotation)}
              onPointerDown={startDrag(onDrag, 'leading')}
            />
            <Bar
              gridArea="1 / 3/ -1"
              cursor={getRotatedCursor(rotation)}
              onPointerDown={startDrag(onDrag, 'trailing')}
            />
            <Handle
              gridArea="w"
              cursor={getRotatedCursor(rotation)}
              onPointerDown={startDrag(onDrag, 'leading')}
            />
            <Handle
              gridArea="e"
              cursor={getRotatedCursor(rotation)}
              onPointerDown={startDrag(onDrag, 'trailing')}
            />
          </>
        )}
      </Box>
    </Box>
  );
}

// Helpers

function getRotatedCursor(rot: number): CSSProperties['cursor'] {
  if (rot > 30 && rot <= 60) return 'nwse-resize'; // ⤡
  if (rot > 60 && rot <= 120) return 'ns-resize'; // ↕
  if (rot > 120 && rot <= 130) return 'nesw-resize'; // ⤢
  if (rot > 130 && rot <= 190) return 'ew-resize'; // ↔
  if (rot > 190 && rot <= 220) return 'nesw-resize'; // ⤢
  if (rot > 220 && rot <= 280) return 'ns-resize'; // ↕
  if (rot > 280 && rot <= 310) return 'nesw-resize'; // ⤡
  return 'ew-resize'; // ↔
}
